import { Box, Card, CardContent, Stack, Typography } from '@mui/material'
import { FC, useCallback } from 'react'
import { IconButton, Icons, ItemImageArea, SkeletonText } from 'utoniq-ui'

import {
  ConsolePreSaleItemSelectedBoxFragment,
  Maybe,
} from '@/clients/utoniq-core/schema'
import { ItemIcon } from '@/components/organisms'
import { ScrollFetcher } from '@/components/organisms/functionals'
import { styleSets } from '@/components/styles'
import { useMessage, useTranslation } from '@/core'
import { Cell, CellFrame } from '@/core/architecture'

import { ItemSelectBoxSkeleton } from './ItemSelectBox'

type RewardItemsCellProp = {
  loading: boolean
  items?: Maybe<ConsolePreSaleItemSelectedBoxFragment[]>
  onRemove: (itemId: string) => Promise<void>
}
export const RewardItemsCell: Cell<RewardItemsCellProp> = ({
  loading,
  items,
  onRemove,
}) => {
  const { t } = useTranslation('consolePreSaleDetail')
  return (
    <CellFrame sx={{ pb: 2 }}>
      {loading && !items?.length ? (
        <Box sx={{ pt: 1, pb: 2 }}>
          <SkeletonText variant="h4" width="50%" />
        </Box>
      ) : (
        <Typography variant="h4" sx={{ pt: 1, pb: 2 }}>
          {t('registrationTicket', '登録チケット')}
        </Typography>
      )}
      <ScrollFetcher
        nodes={items?.map(item => (
          <ItemSelectBox item={item} onRemove={onRemove} />
        ))}
        emptyMessage={t(
          'preSaleItemEmptyMessage',
          '登録されたチケットはありません'
        )}
        loadingComponent={
          <Stack spacing={2} sx={{ pb: 2 }}>
            {Array.from(Array(2).keys()).map(key => (
              <ItemSelectBoxSkeleton key={key} />
            ))}
          </Stack>
        }
        componentProps={{ spacing: 2 }}
        loading={loading}
        hasNextPage={false}
        onFetchMore={async () => {}}
      />
    </CellFrame>
  )
}

type ItemSelectBoxProps = {
  item: ConsolePreSaleItemSelectedBoxFragment
  onRemove: (itemId: string) => Promise<void>
}

const ItemSelectBox: FC<ItemSelectBoxProps> = ({ item, onRemove }) => {
  const { showError } = useMessage()
  const { t } = useTranslation('consolePreSaleDetail')

  const handleRemove = useCallback(async () => {
    await onRemove(item.id).catch(error => showError(error))
  }, [onRemove, item.id, showError])

  return (
    <Card elevation={0}>
      <CardContent sx={{ p: 5, '&:last-child': { pb: 5 } }}>
        <Stack direction="row" spacing={4}>
          <Stack sx={{ position: 'relative', height: 'fit-content' }}>
            <Stack sx={{ zIndex: 1, position: 'absolute', top: 4, left: 4 }}>
              <ItemIcon type={item.type} />
            </Stack>
            <ItemImageArea
              size="small"
              src={item.thumbnail?.front?.smallUri ?? ''}
            />
          </Stack>
          <Stack flex={1} justifyContent="center">
            <Typography variant="h6" sx={{ ...styleSets.lineCamp(2) }}>
              {item.title}
            </Typography>
            <Stack direction="row" sx={{ pt: 1 }}>
              <Stack flex={1}>
                {item.sku?.unitName && (
                  <Typography variant="caption">{item.sku.unitName}</Typography>
                )}

                {/* 購入・使用状況 */}
                <Stack spacing={0} sx={{ mb: 1 }}>
                  <Typography color="text.secondary" variant="body2">
                    {t('purchasedNumber', {
                      purchasedNumber:
                        item.purchasedNumber +
                          (item.pendingPurchaseNumber ?? 0) || '-',
                      issuedNumber: item.issuedNumber || '-',
                    })}
                  </Typography>
                  {!item.metadata?.ticket?.throughTicket?.length && (
                    <Typography color="text.secondary" variant="body2">
                      {t('ticketUsed', {
                        used: item.ticketUsage?.usedCount || '-',
                        unused: item.ticketUsage?.unusedCount || '-',
                      })}
                    </Typography>
                  )}
                  {item.metadata?.ticket?.throughTicket?.map(
                    (throughTicket, index) => (
                      <Typography
                        color="text.secondary"
                        variant="body2"
                        key={index}
                      >
                        {t('throughTicketUsed', {
                          name: throughTicket.name,
                          used:
                            item.throughTicketUsedNumbers?.[index]
                              ?.usedNumber || '-',
                          unused:
                            item.purchasedNumber +
                              (item.pendingPurchaseNumber ?? 0) -
                              (item.throughTicketUsedNumbers?.[index]
                                ?.usedNumber ?? 0) || '-',
                        })}
                      </Typography>
                    )
                  )}
                </Stack>

                {!!item.price && (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {item.paymentType === 'prepaid_point' ||
                    !item.paymentType ? (
                      <>
                        <Icons.Point fontSize="small" />
                        <Typography variant="subtitle1">
                          {item.formattedPrice}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="subtitle1">
                        {t('price', { number: item.formattedPrice })}
                      </Typography>
                    )}
                  </Stack>
                )}
              </Stack>
            </Stack>
          </Stack>
          <Stack spacing={3}>
            <IconButton.Close
              size="xSmall"
              bgcolor="grey.100"
              color="grey.700"
              onClick={handleRemove}
              sx={{ position: 'relative', zIndex: 2 }}
            />
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  )
}
